import React, { useEffect, useState } from 'react';

import CollapsableCard from '../components/CollapsableCard';
import useEnvConfig from '../hooks/useEnvConfig';
import axios from 'axios';
import { css } from '@emotion/core';
import { Typography } from '@material-ui/core';
import Grid from '@material-ui/core/Grid';
import ReportProblemOutlinedIcon from '@material-ui/icons/ReportProblemOutlined';
import CheckCircleOutlineIcon from '@material-ui/icons/CheckCircleOutline';
import ErrorOutlineIcon from '@material-ui/icons/ErrorOutline';
import SecurityIcon from '@material-ui/icons/Security';
import Icon from '@material-ui/core/Icon';
import LinearProgress from '@material-ui/core/LinearProgress';
import PropTypes from 'prop-types';
import { DateTime } from 'luxon';

const SystemStatus = ({ prospectId, hybridAuthToken, siteTimezone, lat, lon, hasBattery, contractStatus }) => {
  const { customerSystemStatusServiceAPIUrl } = useEnvConfig();
  const [systemStatus, setSystemStatus] = useState(null);
  const [systemStatusError, setSystemStatusErrorError] = useState(null);
  const { enableSystemStatus } = useEnvConfig();

  useEffect(() => {
    (async () => {
      try {
        const params = new URLSearchParams();
        if (siteTimezone) params.set('siteTimezone', siteTimezone);
        if (typeof lat === 'number' || (typeof lat === 'string' && lat)) params.set('lat', String(lat));
        if (typeof lon === 'number' || (typeof lon === 'string' && lon)) params.set('lon', String(lon));
        if (typeof hasBattery !== 'undefined') params.set('hasBattery', hasBattery ? 'true' : 'false');
        if (contractStatus) params.set('contractStatus', contractStatus);

        const url = `${customerSystemStatusServiceAPIUrl}/v2/system-status/${prospectId}`;

        const customerSystemStatus = await axios.get(url, {
          headers: {
            Authorization: hybridAuthToken,
          },
          params,
        });

        setSystemStatus(customerSystemStatus.data.systemStatus);
      } catch (error) {
        setSystemStatusErrorError(error);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const solarStatusMapping = {
    none: { icon: CheckCircleOutlineIcon, color: '#038761' },
    minor: { icon: ReportProblemOutlinedIcon, color: '#FFD35C' },
    significant: { icon: ErrorOutlineIcon, color: '#CB2B51' },
    under_review: { icon: SecurityIcon, color: '#003A70' },
  };

  const isHealthy = systemStatus?.solarStatus?.severity === 'none';

  if (!enableSystemStatus) {
    return null;
  }

  // loading
  if (!systemStatusError && !systemStatus) {
    return (
      <CollapsableCard
        title="Solar Health Status"
        css={(theme) =>
          css`
            margin-bottom: ${theme.spacing(2)}px;
          `
        }
      >
        <LinearProgress />
      </CollapsableCard>
    );
  }

  // error
  if (systemStatusError) {
    return (
      <CollapsableCard
        title="Solar Health Status"
        css={(theme) =>
          css`
            margin-bottom: ${theme.spacing(2)}px;
          `
        }
      >
        <Grid container alignItems="center" direction="column">
          <Grid item xs>
            <ErrorOutlineIcon
              css={css`
                color: #cb2b51;
              `}
            />
          </Grid>
          <Grid item xs>
            <Typography variant="h6">{systemStatusError.message || 'Something has gone wrong'}</Typography>
          </Grid>
          <Grid item xs>
            <Typography variant="body1">
              Something has gone wrong in calling the customer system status service
            </Typography>
          </Grid>
        </Grid>
      </CollapsableCard>
    );
  }

  const NoStatusFound = () => {
    return (
      <Grid container direction="column" data-testid="no-status-found">
        <Grid item xs>
          <Typography
            variant="h6"
            css={(theme) =>
              css`
                margin-bottom: ${theme.spacing(2)}px;
              `
            }
          >
            Cannot Retrieve Status
          </Typography>
        </Grid>
        <Grid item xs>
          <Typography variant="body1">
            Solar Health Status is not available at the moment. Please check back later.
          </Typography>
        </Grid>
      </Grid>
    );
  };

  const CustomerFacingStatusCard = () => {
    return (
      <Grid item xs={12} lg={6} data-testid="customer-facing-status-card">
        <CollapsableCard
          title="Solar Health Status: Customer-Facing"
          css={(theme) =>
            css`
              margin-bottom: ${theme.spacing(2)}px;
            `
          }
        >
          {!systemStatus?.solarStatus?.severity ? (
            <NoStatusFound />
          ) : (
            <Grid container direction="row" spacing={3} justifyContent="space-around">
              <Grid item xs>
                <Grid container direction="column" spacing={2}>
                  <Grid item xs>
                    <Grid container direction="row">
                      <Grid
                        container
                        direction="row"
                        css={css`
                          width: auto;
                        `}
                      >
                        <Grid item>
                          <Icon
                            component={solarStatusMapping[systemStatus?.solarStatus?.severity]?.icon}
                            css={css`
                              color: ${solarStatusMapping[systemStatus?.solarStatus?.severity]?.color};
                              height: 32px;
                              width: 32px;
                              margin-right: 4px;
                            `}
                          ></Icon>
                        </Grid>
                        <Grid item>
                          <Typography variant="h6">
                            {systemStatus?.solarStatus?.messageHeader || systemStatus?.solarStatus?.severity}
                          </Typography>
                        </Grid>
                      </Grid>
                    </Grid>
                  </Grid>
                  <Grid item xs>
                    <Typography
                      variant="body1"
                      css={css`
                        white-space: pre-line;
                      `}
                    >
                      {systemStatus?.solarStatus?.customerMessaging}
                    </Typography>
                  </Grid>
                  <Grid item xs>
                    <Grid container direction="row">
                      <Typography
                        css={css`
                          font-size: 16px;
                          font-weight: 700;
                        `}
                      >
                        As of Date:
                      </Typography>
                      <Typography>
                        &nbsp;{DateTime.fromISO(systemStatus?.solarStatus?.lastUpdatedDate).toLocaleString()}
                      </Typography>
                    </Grid>
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          )}
        </CollapsableCard>
      </Grid>
    );
  };

  const InternalUseStatusCard = () => {
    return (
      <Grid item xs={12} lg={6} data-testid="internal-use-status-card">
        <CollapsableCard
          title="Solar Health Status: Internal Only"
          css={(theme) =>
            css`
              margin-bottom: ${theme.spacing(2)}px;
            `
          }
        >
          <Grid container direction="row" spacing={3} justifyContent="space-around">
            <Grid item xs>
              {!systemStatus?.solarStatus?.severity ? (
                <NoStatusFound />
              ) : (
                <Grid container direction="column" spacing={2}>
                  <Grid item xs>
                    <Typography variant="h6">System State</Typography>
                  </Grid>
                  <Grid item xs>
                    <Grid container direction="row">
                      <Typography
                        css={css`
                          font-size: 16px;
                          font-weight: 700;
                        `}
                      >
                        Status:
                      </Typography>
                      <Typography>&nbsp;{systemStatus?.solarStatus?.messageHeader}</Typography>
                    </Grid>
                  </Grid>
                  {!isHealthy && (
                    <Grid item xs>
                      <Grid container direction="column">
                        <Typography
                          css={css`
                            font-size: 16px;
                            font-weight: 700;
                          `}
                        >
                          Details:
                        </Typography>
                        <br />
                        <Typography
                          css={css`
                            white-space: pre-line;
                          `}
                        >
                          {systemStatus?.solarStatus?.spotlightMessaging}
                        </Typography>
                      </Grid>
                    </Grid>
                  )}
                </Grid>
              )}
            </Grid>
          </Grid>
        </CollapsableCard>
      </Grid>
    );
  };

  // happy response
  return (
    <Grid container spacing={2} data-testid="system-status-cards">
      <CustomerFacingStatusCard />
      <InternalUseStatusCard />
    </Grid>
  );
};

SystemStatus.propTypes = {
  prospectId: PropTypes.string.isRequired,
  hybridAuthToken: PropTypes.string.isRequired,
  siteTimezone: PropTypes.string,
  lat: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  lon: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  hasBattery: PropTypes.bool,
  contractStatus: PropTypes.string,
};

export default SystemStatus;
